import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { Label, TextInput } from "flowbite-react";
import { gamesApi } from "../../api/games";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

export default function EditDefaultGamePackage({ isOpen, onClose, refreshGames, gamePackage }) {
  const { tokens, user } = useAuth();
  const [gameSettingsDropdownOpen, setGameSettingsDropdownOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState("");
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [commissionRate, setCommissionRate] = useState("");
  const [winRate, setWinRate] = useState("");
  const [doubleWinRate, setDoubleWinRate] = useState("");
  const [tripleWinRate, setTripleWinRate] = useState("");
  const [gameSettingsId, setGameSettingsId] = useState("");
  const [gameSettingsOptions, setGameSettingsOptions] = useState([]);
  const [thumbnail, setThumbnail] = useState({});

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setMounted(true), 10);
      fetchGameSettings();

      // Pre-populate form with package data
      if (gamePackage) {
        setName(gamePackage.name || "");
        setCommissionRate(gamePackage.commissionRate || 0);
        setWinRate(gamePackage.winRate || 0);
        setDoubleWinRate(gamePackage.doubleWin || gamePackage.doubleWinRate || 0);
        setTripleWinRate(gamePackage.tripleWin || gamePackage.tripleWinRate || 0);
        
        // Handle different property naming in the API response
        const settingsId = gamePackage.game_settings_id || gamePackage.gameSettingsId;
        setGameSettingsId(settingsId || "");
        
        // Find and set the selected game name
        if (gamePackage.game_settings && gamePackage.game_settings.name) {
          setSelectedGame(gamePackage.game_settings.name);
        } else if (gamePackage.gameSettings && gamePackage.gameSettings.name) {
          setSelectedGame(gamePackage.gameSettings.name);
        }
        
        console.log("Populating form with game package data:", gamePackage);
      }
    } else {
      setMounted(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen, gamePackage]);

  const fetchGameSettings = async () => {
    try {
      const response = await gamesApi.getAllGames(tokens.access.token);
      if (response) {
        setGameSettingsOptions(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching game settings:", error);
      toast.error("Failed to load game settings");
      setGameSettingsOptions([]);
    }
  };

  // Helper function to format currency
  const toCurrency = (value) => {
    return parseFloat(value).toFixed(2);
  };

  if (!visible) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Make sure the user is an admin
      if (user.roleId !== 1) {
        toast.error("Only admins can edit default game packages");
        return;
      }

      // Basic validation
      if (!name.trim()) {
        toast.error("Package name is required");
        return;
      }

      if (!gameSettingsId) {
        toast.error("Please select a game type");
        return;
      }

      // Show loading indicator
      const loadingToastId = toast.loading("Updating package...");

      // Create update payload with only the required fields
      const updatePayload = {
        name,
        commissionRate: parseInt(commissionRate) || 0,
        winRate: parseInt(winRate) || 0,
        doubleWin: parseInt(doubleWinRate) || 0,
        tripleWin: parseInt(tripleWinRate) || 0,
        gameSettingsId,
        userId: user.id,
        isPublic: false,
        thumbnail: thumbnail
      };

      console.log("Updating game package with payload:", updatePayload);

      const response = await gamesApi.updateGame(tokens.access.token, gamePackage.id, updatePayload);
      
      // Dismiss loading toast
      toast.dismiss(loadingToastId);

      if (response.status === 200) {
        Swal.fire({
          title: "Success",
          text: "Default game package updated successfully",
          icon: "success",
        });
        onClose();
        // Refresh games list
        if (refreshGames) {
          refreshGames();
        }
      } else {
        Swal.fire({
          title: "Error",
          text: response.data?.message || "Failed to update default game package",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error updating default game package:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Error updating default game package",
        icon: "error",
      });
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center px-4 z-[60] font-poppins"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`w-full max-w-sm bg-white rounded-3xl transform transition-transform duration-300 ${mounted ? "translate-y-0" : "translate-y-full"
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="py-5 bg-[#0E64D1] rounded-t-3xl text-white relative">
          <h2 className="font-bold text-center">Edit Default Game Package</h2>
          <button
            className="absolute right-4 top-5 text-white"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-4">
          <div className="my-3">
            <Label htmlFor="name">Package Name</Label>
            <TextInput
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Package name"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="my-3 relative">
            <Label>Game Type</Label>
            <button
              id="gameSettings"
              onClick={(e) => {
                e.preventDefault();
                setGameSettingsDropdownOpen(!gameSettingsDropdownOpen);
              }}
              className="w-full text-sm px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none flex justify-between"
            >
              <span className="font-light text-sm text-gray-500">
                {selectedGame || "Game Type"}
              </span>
              {gameSettingsDropdownOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            {gameSettingsDropdownOpen && (
              <ul className="absolute w-full mt-1 bg-white border rounded-md shadow-md z-10 max-h-60 overflow-y-auto">
                {Array.isArray(gameSettingsOptions) && gameSettingsOptions.map((gameSetting, index) => (
                  <li
                    key={gameSetting.id}
                    onClick={() => {
                      setSelectedGame(gameSetting.name);
                      setGameSettingsId(gameSetting.id);
                      setGameSettingsDropdownOpen(false);
                      if (typeof gameSetting.otherSettings.image === 'string') {
                        setThumbnail({ url: gameSetting.otherSettings.image });
                      } else {
                        setThumbnail(gameSetting.otherSettings.image || {});
                      }
                    }}
                    className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${
                      index !== 0 ? "border-t border-[#E1E7EF]" : ""
                    }`}
                  >
                    {gameSetting.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="my-3">
            <Label>Commission Rate (%)</Label>
            <TextInput
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              placeholder="0"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="my-3">
            <Label>Win Rate</Label>
            <TextInput
              value={winRate}
              onChange={(e) => setWinRate(e.target.value)}
              placeholder="0"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="my-3">
            <Label>Double Win Rate</Label>
            <TextInput
              value={doubleWinRate}
              onChange={(e) => setDoubleWinRate(e.target.value)}
              placeholder="0"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="my-3">
            <Label>Triple Win Rate</Label>
            <TextInput
              value={tripleWinRate}
              onChange={(e) => setTripleWinRate(e.target.value)}
              placeholder="0"
              className="w-full rounded-md"
              required
            />
          </div>

          <div className="flex gap-3 mb-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 p-2 border border-blue-600 text-blue-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 p-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              Update Package
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 